// Component ids that a merchant is actually allocated to for one departure.
export const getAllocatedComponentIds = (merchantAllocations, departureDateId, packageId) => {
    const ids = new Set();
    if (!departureDateId) return ids;

    (merchantAllocations || []).forEach((alloc) => {
        if (alloc.departure_date_id !== departureDateId) return;
        if (alloc.is_active !== true) return;
        if (alloc.trip_package_id !== packageId) return;
        if (String(alloc.status || "").toLowerCase() !== "accepted") return;
        if (alloc.package_component_id != null) ids.add(alloc.package_component_id);
    });
    return ids;
};

export const EMPTY_OFFERINGS = {
    base_accommodation: [],
    base_services: [],
    accommodation_addons: [],
    other_addons: [],
};

export const inferAddonCategory = (addon, linkedService) =>
    linkedService?.category || addon.category || "other";

export const addonTypeOf = (addon) =>
    String(addon?.type || addon?.addon_type || "").trim().toLowerCase();

// Arrive Early / Stay Behind have no Attach Component — they use extension
// days instead, so merchant allocation cannot gate them on the order screen.
export const isStandaloneExtensionAddon = (addon) => {
    const t = addonTypeOf(addon);
    return t === "arrive_early" || t === "stay_behind";
};

export const isOfferedAddon = (addon, allocatedIds) => {
    if ((addon.status || "").toLowerCase() !== "active") return false;
    if (addon.linked_component_id == null) {
        return isStandaloneExtensionAddon(addon);
    }
    return allocatedIds.has(addon.linked_component_id);
};

// Groups add-ons and included services for one departure.
// Linked add-ons must be Active and their service allocated. Arrive Early /
// Stay Behind are Active-only (no linked component / allocation).
export const deriveOfferings = ({ services, addons, itineraryDays, allocatedIds }) => {
    const allAddons = Array.isArray(addons) ? addons : [];
    const serviceRows = Array.isArray(services) ? services : [];
    const days = Array.isArray(itineraryDays) ? itineraryDays : [];
    const allocated = allocatedIds || new Set();

    // Computed from ALL addons (not filtered) so an addon-linked service
    // never leaks into "included", even when its addon is hidden.
    const linkedComponentIds = new Set(
        allAddons.map((a) => a.linked_component_id).filter((id) => id != null)
    );

    const addons_services = allAddons
        .filter((addon) => isOfferedAddon(addon, allocated))
        .map((addon) => {
            const linkedService = serviceRows.find((s) => s.id === addon.linked_component_id) || null;
            if (addon.linked_component_id != null && !linkedService) return null;
            return {
                addon,
                linkedService,
                category: inferAddonCategory(addon, linkedService),
            };
        })
        .filter(Boolean);

    const isBase = (service) => !linkedComponentIds.has(service.id) && allocated.has(service.id);

    const dayServicesOf = (day) =>
        (day.line_items || [])
            .map((li) => serviceRows.find((s) => s.id === li.trip_component_id))
            .filter(Boolean)
            .filter((s, i, arr) => arr.findIndex((x) => x.id === s.id) === i);

    const specificDayDayIds = (isAccommodation) =>
        new Set(
            addons_services
                .filter((i) => (i.category === "accommodation") === isAccommodation
                    && i.addon.application_scope === "specific_day"
                    && i.addon.itinerary_day_id != null)
                .map((i) => i.addon.itinerary_day_id)
        );

    const accDayIds = specificDayDayIds(true);
    const otherDayIds = specificDayDayIds(false);

    const base_accommodation = days
        .map((day) => ({
            day_id: day.id,
            day_number: day.day_number,
            day_title: day.day_title,
            accommodations: dayServicesOf(day).filter((s) => s.category === "accommodation" && isBase(s)),
        }))
        .filter((d) => d.accommodations.length > 0 || accDayIds.has(d.day_id));

    const base_services = days
        .map((day) => ({
            day_id: day.id,
            day_number: day.day_number,
            day_title: day.day_title,
            services: dayServicesOf(day).filter((s) => s.category !== "accommodation" && isBase(s)),
        }))
        .filter((d) => d.services.length > 0 || otherDayIds.has(d.day_id));

    return {
        base_accommodation,
        base_services,
        accommodation_addons: addons_services.filter((i) => i.category === "accommodation"),
        other_addons: addons_services.filter((i) => i.category !== "accommodation"),
    };
};
